import { PlanogramObjectAttributes } from "./Resources";
import { PlanogramObject, PlanogramObjectShaderDataManager } from "./PlanogramObject.js";
import { PlanogramSubcomponent } from "./PlanogramSubcomponent.js";
import { PlanogramDivider } from "./PlanogramDivider.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramContainer } from "./PlanogramContainer.js";

import * as THREE from 'three';


export class PlanogramComponent extends PlanogramObject {

    //#region Attributes 
    static Attributes = {
        "Type": "type",
        "EditCustom": "edit-custom"
    };
    
    //#endregion

    static get observedAttributes(){
        return [
            ...Object.values(PlanogramComponent.Attributes),
            ...Object.values(PlanogramObjectAttributes.Attributes),
        ];
    }

    get groupBoundingBox() {
        return this.#subComponents.reduce( (a,c) => a.union(c.groupBoundingBox), new THREE.Box3());
    }

    get mainBlockBoundingBox() {
        return this.#subComponents.reduce((a, c) => a.union(c.mainBlockBoundingBox), new THREE.Box3());
    }

    get type() { 
        if(!this.hasAttribute(PlanogramComponent.Attributes.Type)) return PlanogramComponent.Types.Component;
        var attributeval = this.getAttribute(PlanogramComponent.Attributes.Type);
        for(var [key, value] of Object.entries(PlanogramComponent.Types)){
            if(key.toLocaleLowerCase() === attributeval.toLocaleLowerCase())
                return value;
        }
        return PlanogramComponent.Types.Component; 
    }

    get editCustom() { return this.hasAttribute(PlanogramComponent.Attributes.EditCustom); }
    get registryValues(){ return this.#subComponents.reduce( (a,c) =>[...a, ...c.registryValues], []); }

    /** @returns {PlanogramSubcomponent[]}  */
    get #subComponents(){
        return [...this.querySelectorAll("planogram-subcomponent")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender && x.isConnected);
    }

    get totalMeshCount(){
        return this.#subComponents.reduce( (a,c) => a + c.totalMeshCount, 0);
    } 

    /** @type {THREE.BufferGeometry} */
    #geometry = null;

    /** @type {THREE.InstancedMesh} */      
    #mesh = null;

    constructor(){
        super();
    }


    #generateGeometry() {
        this.#geometry?.dispose();
        this.#geometry = new THREE.BoxGeometry(1, 1, 1);
        this.#geometry.computeBoundingBox();
        this.#geometry.center();
    }

    #generateMeshes(){
        
        if(!this.#mesh || this.#mesh.count !== this.totalMeshCount ){
            if (this.#mesh) this.#mesh.removeFromParent();
            const shader = PlanogramContainer.get(this).subComponentShader;
            this.#mesh = new THREE.InstancedMesh( this.#geometry, shader, this.totalMeshCount )
            this.addMesh(this.#mesh);
        }

        const subComponents = this.#subComponents;
        if(!subComponents.length) return;

        subComponents.reduce((a,c)=>{
            c.generateSubcomponent(this.#mesh, a );
            return a + c.totalMeshCount;
        }, 0);

        this.setDecoratorGeometrySize( true );

        this.#setAttributes( true );
        
        if (this.selected || this.#subComponents.some(s => s.selected))
            PlanogramContainer.get(this)?.refreshRotationGizmo();
    }

    #setAttributes( refreshAllComponents = false ){
        const container = PlanogramContainer.get(this);
        container.updateObjectDataManager();

        const subcomponents = [...container.querySelectorAll("planogram-subcomponent")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender && x.isConnected);
        if(subcomponents.length === 0) return;

        const blockIndices = PlanogramObjectShaderDataManager.buildBlockIndices(subcomponents);

        if(!refreshAllComponents){
            this.#refreshTexelPositions(blockIndices);
            return;
        }

        const components = [...container.querySelectorAll("planogram-component")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender && x.isConnected);
        components.forEach(component => component.#refreshTexelPositions(blockIndices));
    }

    /**
     * @param {Map<PlanogramSubcomponent|PlanogramDivider, number>} blockIndices
     */
    #refreshTexelPositions( blockIndices ){
        if(!this.#mesh) return;
        const vectors = this.#subComponents.flatMap( (subComponent) => {
            const index = blockIndices.get(subComponent);
            const dividers = subComponent.dividers;
            let dividerCursor = 0;
            return subComponent.partTypes.flatMap(type=>{
                if(type === PlanogramComponent.Types.Divider){
                    const divider = dividers[dividerCursor++];
                    return [blockIndices.get(divider), type];
                }
                return [index,type];
            });
        });

        this.#mesh.geometry.setAttribute("texelPositionAndType", new THREE.InstancedBufferAttribute(new Float32Array(vectors), 2));
        this.#mesh.instanceMatrix.needsUpdate = true;
    }

    #pendingRemesh = false;
    #plugFlushScheduled = false;

    subComponentPlug( reMesh = false ){
        if(!this.isConnected) return;
        this.#pendingRemesh ||= reMesh;
        if(this.#plugFlushScheduled) return;
        this.#plugFlushScheduled = true;
        queueMicrotask(() => {
            this.#plugFlushScheduled = false;
            const remesh = this.#pendingRemesh;
            this.#pendingRemesh = false;
            if(!this.isConnected) return;
            if(remesh) this.#generateMeshes();
            else this.#setAttributes();
        });
    }

    /**
     * 
     * @param {THREE.Intersection} intersection 
     */
    getTargetedSubComponent( intersection )
    {
        if(!intersection || !intersection.object || intersection.object !== this.#mesh) return null;
        const instanceId = intersection.instanceId;
        if(instanceId === undefined || instanceId < 0) return null;
        let count = 0;
        for(const subComponent of this.#subComponents){
            if(instanceId < count + subComponent.totalMeshCount) return subComponent;
            count += subComponent.totalMeshCount;
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.firstRender = false;
        this.#generateGeometry();
        this.#generateMeshes();

    }


    attributeChangedCallback(name, oldValue, newValue) {
        if (this.firstRender || oldValue === newValue ) return;
        super.attributeChangedCallback(name, oldValue, newValue);
        if(name === PlanogramComponent.Attributes.Type){
            this.#generateMeshes();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    dispose(){
        super.dispose();
        this.#mesh.removeFromParent();
        this.#mesh = null;
        this.#geometry.dispose();
        this.#geometry = null;
    }

    //#region Static Properties
    static Types = {
        Component : 0,
        Shelf_Riser : 1,
        Divider: 2,
        PegProng: 3,
        PegTag: 4,
        Chest_Wall_Bottom: 5,
        Chest_Wall_Top: 6,
        Chest_Wall_Left: 7,
        Chest_Wall_Right: 8,
        Chest_Wall_Front: 9,
        Chest_Wall_Back: 10,
        Chest_Notch_Plane: 11,
    }
    //#endregion

}